/**
 * Product rollout scope (Bill 2026-10-06, Peter: business products must not look clickable
 * in Phase 1). A product is in this phase when at least one entity type from an ENABLED
 * entity category (rollout.enabledEntityGroups) can be its primary owner under the product's
 * own catalog rules (allowedCallClasses, customerTypes, businessOnly). So business and
 * public-funds products drop out while only Personal is enabled, and come back by
 * themselves when Admin enables those categories — no product list to maintain.
 */
import type { Product } from '@retail-ops/shared-types';
import { callClassFor } from '@retail-ops/shared-types';
import { ENTITY_GROUPS } from './entityGroups';
import { rolloutAllows } from '../../utils/rolloutScope';

export function enabledEntityTypes(enabledGroupsRaw: unknown): ReadonlyArray<string> {
  return ENTITY_GROUPS
    .filter((g) => rolloutAllows(enabledGroupsRaw, g.key))
    .flatMap((g) => g.types.map((t) => t.type));
}

const PERSONAL_TYPES: ReadonlySet<string> = new Set(
  ENTITY_GROUPS.find((g) => g.key === 'personal')?.types.map((t) => t.type) ?? [],
);

export function productInRolloutScope(product: Product, enabledTypes: ReadonlyArray<string>): boolean {
  const p = product as Product & {
    allowedCallClasses?: ReadonlyArray<string> | null;
    customerTypes?: ReadonlyArray<string> | null;
    businessOnly?: boolean | null;
  };
  return enabledTypes.some((t) => {
    if (p.businessOnly && PERSONAL_TYPES.has(t)) return false;
    if (p.allowedCallClasses && p.allowedCallClasses.length > 0
        && !p.allowedCallClasses.includes(callClassFor(t as never) as string)) return false;
    if (p.customerTypes && p.customerTypes.length > 0 && !p.customerTypes.includes(t)) return false;
    return true;
  });
}
